import { useQueryClient } from '@tanstack/react-query';
import useMutationApi from '../useMutationApi';
import { RebateAgreementDTO } from './types';

interface CreateRebateAgreementRequest {
  // V2: All optional - backend extracts from PDF
  supplierId?: string;
  files?: File[];
  startDate?: string;
  endDate?: string;
  model?: string;
  agreementName?: string;
  manualMode?: boolean;
  replaceExistingAgreementId?: string;
}

/**
 * V2 hook - Optimized for faster PDF processing
 *
 * Uploads files first to get URLs, then passes URLs to OCR service
 * This skips the slow PDF-to-image conversion step
 */
const useCreateRebateAgreementV2 = () => {
  const queryClient = useQueryClient();

  return useMutationApi<CreateRebateAgreementRequest, RebateAgreementDTO>({
    endpoint: 'rebates/agreements/v2', // V2 endpoint with URL optimization
    method: 'POST',
    transformRequest: (body: CreateRebateAgreementRequest) => {
      const formData = new FormData();

      // V2: All fields optional - backend extracts from PDF
      if (body.supplierId) {
        formData.append('supplierId', body.supplierId);
      }
      if (body.files && body.files.length > 0) {
        body.files.forEach((file) => {
          formData.append('files', file);
        });
      }
      if (body.startDate) {
        formData.append('startDate', body.startDate);
      }
      if (body.endDate) {
        formData.append('endDate', body.endDate);
      }
      if (body.model) {
        formData.append('model', body.model);
      }
      if (body.agreementName) {
        formData.append('agreementName', body.agreementName);
      }
      if (body.manualMode !== undefined) {
        formData.append('manualMode', body.manualMode.toString());
      }
      if (body.replaceExistingAgreementId) {
        formData.append(
          'replaceExistingAgreementId',
          body.replaceExistingAgreementId,
        );
      }
      return formData;
    },
    options: {
      onSuccess: () => {
        // Invalidate rebate agreements queries to refresh the data
        queryClient.invalidateQueries({ queryKey: ['rebateAgreements'] });
      },
    },
    successMessage: 'Rebate agreement uploaded successfully',
  });
};

export default useCreateRebateAgreementV2;
